import React from "react";

import range from "lodash/range";
import { useRouter } from "next/router";
import styled from "styled-components";

import { Button } from "~/components/Button";

const PageNumberBox = styled.a`
  background: white;
  cursor: pointer;
`;

const EllipsisBox = styled.div`
  font-size: 20px;
  width: 40px;
  text-align: center;
`;

type PaginationProps = {
  totalPages?: number;
  maxPagesToShow?: number;
};

export default function Pagination({ totalPages = 1, maxPagesToShow = 10 }: PaginationProps) {
  const router = useRouter();
  const currentPage = (parseInt(router.query.currentPage as string) || 0) + 1;
  const isLastPage = currentPage >= totalPages;

  if (totalPages === 1) return null;

  const handleChangePage = (newPage: number) => {
    const newRouterQuery = { ...router.query, currentPage: String(newPage - 1) };
    router.replace({ pathname: router.pathname, query: newRouterQuery }, undefined, { scroll: false });
  };

  const generatePageNumbers = () => {
    if (totalPages <= maxPagesToShow) {
      return range(1, totalPages + 1);
    }

    const pages: (number | string)[] = [];
    const halfMax = Math.floor(maxPagesToShow / 2);
    const startPage = Math.max(1, currentPage - halfMax);
    const endPage = Math.min(totalPages, currentPage + halfMax);

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push("...");
      }
    }

    pages.push(...range(startPage, endPage + 1));

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <nav className="pagination is-centered" role="navigation" aria-label="pagination">
      <Button
        weight="semibold"
        className="pagination-previous"
        disabled={currentPage === 1}
        onClick={() => handleChangePage(currentPage - 1)}
      >
        Previous
      </Button>
      <Button
        weight="semibold"
        className="pagination-next"
        disabled={isLastPage}
        onClick={() => handleChangePage(currentPage + 1)}
      >
        Next
      </Button>
      <ul className="pagination-list">
        {generatePageNumbers().map((page, index) => (
          <li key={index}>
            {typeof page === "number" ? (
              <PageNumberBox
                className={`pagination-link ${currentPage === page ? "is-current" : ""}`}
                onClick={() => handleChangePage(page)}
              >
                {page}
              </PageNumberBox>
            ) : (
              <EllipsisBox>&hellip;</EllipsisBox>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}
